import {
  PlatformClientContentPartsFragment,
  PlatformClientFolderPartsFragment
} from '@cma/generated/graphql'
import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState
} from 'react'
import { FileSortEnum, useFileQuery } from './useFileQuery'
import { useViewMode, ViewMode } from './useViewMode'
import { useContentPermissions } from './useContentPermissions'

const ContentContext = createContext<{
  currentFolderId: string | null
  currentFolder: PlatformClientFolderPartsFragment | null
  contents: (
    | PlatformClientContentPartsFragment
    | PlatformClientFolderPartsFragment
  )[]
  filteredContents: (
    | PlatformClientContentPartsFragment
    | PlatformClientFolderPartsFragment
  )[]
  isAllSelected: boolean
  isRenaming: boolean
  isDeleting: boolean
  isMoving: boolean
  movingFileIds: string[]
  search: string
  sortKey: FileSortEnum
  selectedContents: string[]
  viewMode: ViewMode
  uploadRef: React.RefObject<HTMLInputElement>
  createNewFolder: boolean
  renamingFileId: string | null
  hasContentPermissions: boolean
  previewContent: PlatformClientContentPartsFragment | null
  setPreviewContent: React.Dispatch<
    React.SetStateAction<PlatformClientContentPartsFragment | null>
  >
  setIsDeleting: React.Dispatch<React.SetStateAction<boolean>>
  setIsMoving: React.Dispatch<React.SetStateAction<boolean>>
  setCreateNewFolder: React.Dispatch<React.SetStateAction<boolean>>
  setMovingFileIds: React.Dispatch<React.SetStateAction<string[]>>
  deselectAllFiles: () => void
  navigateToFolder: (folderId: string | null) => void
  selectAllFiles: () => void
  setIsRenaming: (isRenaming: boolean) => void
  setSearch: React.Dispatch<React.SetStateAction<string>>
  setSortKey: React.Dispatch<React.SetStateAction<FileSortEnum>>
  toggleFileSelection: (fileId: string) => void
  toggleViewMode: () => void
  setRenamingFileId: (fileId: string | null) => void
}>({
  currentFolderId: null,
  currentFolder: null,
  contents: [],
  filteredContents: [],
  isAllSelected: false,
  isRenaming: false,
  isDeleting: false,
  isMoving: false,
  movingFileIds: [],
  search: '',
  sortKey: FileSortEnum.All,
  selectedContents: [],
  viewMode: 'grid',
  renamingFileId: null,
  uploadRef: { current: null },
  createNewFolder: false,
  hasContentPermissions: false,
  previewContent: null,
  setPreviewContent: () => {},
  setCreateNewFolder: () => {},
  deselectAllFiles: () => {},
  setIsDeleting: () => {},
  setIsMoving: () => {},
  setMovingFileIds: () => {},
  navigateToFolder: () => {},
  selectAllFiles: () => {},
  setIsRenaming: () => {},
  setSearch: () => {},
  setSortKey: () => {},
  toggleFileSelection: () => {},
  toggleViewMode: () => {},
  setRenamingFileId: () => {}
})

export function ContentProvider({ children }: { children: React.ReactNode }) {
  const { viewMode, toggleViewMode } = useViewMode()

  const [isDeleting, setIsDeleting] = useState(false)
  const [isRenaming, setIsRenaming] = useState(false)
  const [isMoving, setIsMoving] = useState(false)
  const [createNewFolder, setCreateNewFolder] = useState(false)
  const [movingFileIds, setMovingFileIds] = useState<string[]>([])
  const [renamingFileId, setRenamingFileId] = useState<string | null>(null)
  const [previewContent, setPreviewContent] =
    useState<PlatformClientContentPartsFragment | null>(null)

  const {
    contents,
    filteredContents,
    search,
    sortKey,
    selectedContents,
    isAllSelected,
    currentFolderId,
    currentFolder,
    setSearch,
    setSortKey,
    selectAllFiles,
    deselectAllFiles,
    toggleFileSelection,
    navigateToFolder
  } = useFileQuery()

  const { hasContentPermissions } = useContentPermissions()

  useEffect(() => {
    if (contents && movingFileIds.length > 0) {
      const stillMoving = movingFileIds.some((id) =>
        contents.some((content) => content.id === id)
      )

      if (!stillMoving) {
        setIsMoving(false)
        setMovingFileIds([])
      }
    }
  }, [contents, movingFileIds])

  const uploadRef = useRef<HTMLInputElement>(null)

  return (
    <ContentContext.Provider
      value={{
        contents,
        filteredContents,
        createNewFolder,
        uploadRef,
        viewMode,
        search,
        sortKey,
        selectedContents,
        isAllSelected,
        currentFolderId,
        currentFolder,
        isRenaming,
        isDeleting,
        isMoving,
        movingFileIds,
        renamingFileId,
        hasContentPermissions,
        previewContent,
        setPreviewContent,
        setCreateNewFolder,
        setIsDeleting,
        setIsMoving,
        setMovingFileIds,
        toggleViewMode,
        setSearch,
        setSortKey,
        setIsRenaming,
        selectAllFiles,
        deselectAllFiles,
        toggleFileSelection,
        navigateToFolder,
        setRenamingFileId
      }}>
      {children}
    </ContentContext.Provider>
  )
}

export function useContent() {
  const context = useContext(ContentContext)
  if (context === undefined) {
    throw new Error('useContent must be used within a ContentProvider')
  }
  return context
}
